/**
 * useResponsaveisCrm — usuários ATIVOS da unidade que podem ser responsáveis
 * por lead, tarefa ou compromisso (CRM-22).
 *
 * Fonte única: GET /api/crm/responsaveis (exige só `lead:view`). Substitui
 * a lista derivada dos cards do board (vendedor novo nunca aparecia; com um
 * filtro de responsável ativo só sobrava ele) e o /api/usuarios do cockpit
 * (403 para quem não tem `usuario:view`, e o select vazio atribuía o lead a
 * quem clicava).
 *
 * Cache module-level com dedupe: Novo Lead, Filtros e cockpit montam o hook
 * várias vezes na mesma sessão sem repetir a request a cada abertura.
 */
import { useCallback, useEffect, useState } from "react";

import { useAuth } from "../context/AuthContext";

export const ENDPOINT_RESPONSAVEIS_CRM = "/api/crm/responsaveis";
const DEDUPE_MS = 60_000;

let cache = { lista: [], em: 0 };

/** Só o que o contrato promete: {id, nome, foto_url}. */
function normalizar(payload) {
  if (!Array.isArray(payload)) return [];
  return payload
    .filter((u) => u && u.id)
    .map((u) => ({ id: String(u.id), nome: u.nome || "", foto_url: u.foto_url || null }));
}

/** Zera o cache (teste ou quando um usuário é criado/desativado na sessão). */
export function limparCacheResponsaveisCrm() {
  cache = { lista: [], em: 0 };
}

export default function useResponsaveisCrm({ enabled = true } = {}) {
  const { request, authenticated, hasPermission } = useAuth();
  const ativo =
    Boolean(enabled)
    && Boolean(authenticated)
    && typeof hasPermission === "function"
    && hasPermission("lead:view");
  const [responsaveis, setResponsaveis] = useState(cache.lista);
  const [carregando, setCarregando] = useState(false);

  const carregar = useCallback(
    async (forcar = false) => {
      if (!ativo) return;
      if (!forcar && Date.now() - cache.em < DEDUPE_MS) {
        setResponsaveis(cache.lista);
        return;
      }
      setCarregando(true);
      try {
        const payload = await request(ENDPOINT_RESPONSAVEIS_CRM, { silentError: true });
        const lista = normalizar(payload);
        cache = { lista, em: Date.now() };
        setResponsaveis(lista);
      } catch {
        // Rede ou backend atrás: fica com a última lista conhecida (o select
        // continua utilizável; o backend valida o id no envio).
      } finally {
        setCarregando(false);
      }
    },
    [ativo, request],
  );

  useEffect(() => {
    carregar();
  }, [carregar]);

  return { responsaveis, carregando, recarregar: () => carregar(true) };
}
